import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { archiveApi } from '../../services/api';
import './ViewDocumentModal.css';

export interface StoredDocumentFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  rawDate?: string;
  expiryDate?: string;
  documentDate?: string;
  url?: string;
  type?: string;
}

export interface ViewDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  workplaceTitle: string;
  docTitle: string;
  docCode: string;
  files: StoredDocumentFile[];
  onDeleteFile: (docCode: string, fileId: string) => void;
  onDeleteAllFiles: (docCode: string) => void;
  onAddNewFiles: (docCode: string) => void;
}

const ViewDocumentModal: React.FC<ViewDocumentModalProps> = ({
  isOpen,
  onClose,
  workplaceTitle,
  docTitle,
  docCode,
  files,
  onDeleteFile,
  onDeleteAllFiles,
  onAddNewFiles
}) => {
  const [selectedPreviewFile, setSelectedPreviewFile] = useState<StoredDocumentFile | null>(
    files[0] || null
  );
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<boolean>(false);

  useEffect(() => {
    if (files.length > 0) {
      // Eğer mevcut seçili dosya artık listede yoksa ilk dosyayı seç
      if (!selectedPreviewFile || !files.some(f => f.id === selectedPreviewFile.id)) {
        setSelectedPreviewFile(files[0]);
      }
    } else {
      setSelectedPreviewFile(null);
    }
  }, [files, selectedPreviewFile]);

  // Seçili dosya değiştikçe veya modal açıldıkça blob çek
  useEffect(() => {
    let activeUrl: string | null = null;
    if (isOpen && selectedPreviewFile && selectedPreviewFile.id) {
      const numId = parseInt(selectedPreviewFile.id, 10);
      if (!isNaN(numId)) {
        setPreviewLoading(true);
        setPreviewError(false);
        archiveApi.previewDocument(numId)
          .then((url) => {
            activeUrl = url;
            setBlobUrl(url);
            setPreviewLoading(false);
          })
          .catch((err) => {
            console.error('Önizleme yüklenemedi:', err);
            setPreviewError(true);
            setPreviewLoading(false);
          });
      }
    } else {
      setBlobUrl(null);
    }

    return () => {
      if (activeUrl) {
        window.URL.revokeObjectURL(activeUrl);
      }
      setBlobUrl(null);
    };
  }, [isOpen, selectedPreviewFile]);

  if (!isOpen) return null;

  const handleDeleteSingle = (file: StoredDocumentFile) => {
    Swal.fire({
      target: document.body,
      title: 'Evrak Silinsin mi?',
      text: `"${file.name}" dosyasını silmek istediğinize emin misiniz?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'Vazgeç',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '999999';
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onDeleteFile(docCode, file.id);
      }
    });
  };

  const handleDeleteAll = () => {
    Swal.fire({
      target: document.body,
      title: 'Tüm Belgeler Silinsin mi?',
      text: `"${docTitle}" altındaki tüm evraklar silinecek ve belgenin durumu tekrar HAZIRLANACAK durumuna dönecektir.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Tümünü Sil',
      cancelButtonText: 'Vazgeç',
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = '999999';
      }
    }).then((result) => {
      if (result.isConfirmed) {
        onDeleteAllFiles(docCode);
        onClose();
      }
    });
  };

  return (
    <div className="view-doc-overlay" onClick={onClose}>
      <div className="view-doc-modal" onClick={e => e.stopPropagation()}>
        {/* Üst Başlık — Yeşil / Başarılı Tema */}
        <div className="view-doc-header">
          <div className="view-header-left">
            <span className="view-header-badge">✅ HAZIR İSG EVRAKI</span>
            <h2 className="view-header-title">{docTitle}</h2>
          </div>
          <button
            type="button"
            className="btn-view-close"
            onClick={onClose}
            aria-label="Kapat"
          >
            ✕
          </button>
        </div>

        {/* İçerik */}
        <div className="view-doc-body">
          {/* İşyeri Bilgi Şeridi */}
          <div className="view-workplace-strip">
            <span>🏢 <strong>İşyeri:</strong> {workplaceTitle}</span>
            <span className="view-doc-count-tag">
              Toplam <strong>{files.length}</strong> Dosya
            </span>
          </div>

          {files.length === 0 ? (
            <div className="empty-files-box">
              <span className="empty-icon">📭</span>
              <p>Bu belge için yüklü dosya bulunmamaktadır.</p>
              <button
                type="button"
                className="btn-saas-primary pink small"
                onClick={() => {
                  onClose();
                  onAddNewFiles(docCode);
                }}
              >
                + Yeni Dosya Yükle
              </button>
            </div>
          ) : (
            <div className="view-doc-grid">
              {/* Sol: Dosyalar Listesi */}
              <div className="files-sidebar">
                <div className="sidebar-header">
                  <span>Yüklenen Dosyalar</span>
                  <button
                    type="button"
                    className="btn-add-more-small"
                    onClick={() => {
                      onClose();
                      onAddNewFiles(docCode);
                    }}
                    title="Bu belgeye daha fazla dosya ekle"
                  >
                    + Dosya Ekle
                  </button>
                </div>

                <div className="files-sidebar-list">
                  {files.map((file) => {
                    const isSelected = selectedPreviewFile?.id === file.id;
                    const isPdf = file.name.toLowerCase().endsWith('.pdf');
                    const isImg = /\.(jpg|jpeg|png|webp|gif)$/i.test(file.name);

                    return (
                      <div
                        key={file.id}
                        className={`sidebar-file-item ${isSelected ? 'active' : ''}`}
                        onClick={() => setSelectedPreviewFile(file)}
                      >
                        <div className="sidebar-file-icon">
                          {isPdf ? '📕' : isImg ? '🖼️' : '📄'}
                        </div>
                        <div className="sidebar-file-info">
                          <span className="sidebar-file-name" title={file.name}>
                            {file.name}
                          </span>
                          <span className="sidebar-file-meta">
                            {(file.size / 1024).toFixed(1)} KB • {file.uploadedAt}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-file-del-mini"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSingle(file);
                          }}
                          title="Bu dosyayı sil"
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sağ: Dosya Önizleme / Detay Kutusu */}
              <div className="files-preview-pane">
                {selectedPreviewFile ? (
                  <div className="preview-content-box">
                    <div className="preview-top-toolbar">
                      <div className="preview-title-wrap">
                        <strong>{selectedPreviewFile.name}</strong>
                        <span className="preview-filesize">
                          ({(selectedPreviewFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <div className="preview-actions-wrap">
                        {selectedPreviewFile.url && (
                          <a
                            href={selectedPreviewFile.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-saas-secondary small"
                            download={selectedPreviewFile.name}
                          >
                            ⬇️ İndir
                          </a>
                        )}
                        <button
                          type="button"
                          className="btn-saas-danger-solid small"
                          onClick={() => handleDeleteSingle(selectedPreviewFile)}
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    </div>

                    <div className="preview-display-area">
                      {previewLoading ? (
                        <div className="preview-generic-file" style={{ border: 'none', background: 'transparent' }}>
                          <div className="generic-icon" style={{ animation: 'spin 1s linear infinite' }}>⏳</div>
                          <h4>Belge yükleniyor...</h4>
                          <p>Lütfen bekleyin, evrak hazırlanıyor.</p>
                        </div>
                      ) : previewError ? (
                        <div className="preview-generic-file">
                          <div className="generic-icon">⚠️</div>
                          <h4>Önizleme Alınamadı</h4>
                          <p>Bu dosya doğrudan tarayıcıda açılamadı veya oturum süresi doldu.</p>
                          {selectedPreviewFile.url && (
                            <a
                              href={selectedPreviewFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-saas-secondary small"
                            >
                              🔗 Doğrudan Bağlantıyı Dene
                            </a>
                          )}
                        </div>
                      ) : (blobUrl || selectedPreviewFile.url) && /\.(jpg|jpeg|png|webp|gif|bmp|tiff|heic|heif)$/i.test(selectedPreviewFile.name) ? (
                        <img
                          src={blobUrl || selectedPreviewFile.url}
                          alt={selectedPreviewFile.name}
                          className="preview-image"
                        />
                      ) : (blobUrl || selectedPreviewFile.url) && selectedPreviewFile.name.toLowerCase().endsWith('.pdf') ? (
                        <iframe
                          src={blobUrl || selectedPreviewFile.url}
                          title={selectedPreviewFile.name}
                          className="preview-pdf-frame"
                        />
                      ) : (
                        <div className="preview-generic-file">
                          <div className="generic-icon">📑</div>
                          <h4>{selectedPreviewFile.name}</h4>
                          <p>Ofis veya özel formatlı belge. Görüntülemek için bilgisayarınıza indirebilir veya yeni sekmede açabilirsiniz.</p>
                          {selectedPreviewFile.url && (
                            <a
                              href={selectedPreviewFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-saas-primary green small"
                            >
                              🔍 Dosyayı Yeni Pencerede Aç
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="preview-placeholder">
                    <span>Lütfen soldaki listeden bir dosya seçin</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Alt Eylemler */}
          <div className="view-doc-footer">
            {files.length > 0 && (
              <button
                type="button"
                className="btn-saas-danger-solid"
                onClick={handleDeleteAll}
              >
                🗑️ Belgenin Tüm Dosyalarını Sil
              </button>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                type="button"
                className="btn-saas-secondary"
                onClick={onClose}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDocumentModal;
